import _ from 'lodash'
import client, {fetch, post, put, remove} from '@/base-http'
import {PostPagination, Post, PostPaginationFilters, Tag} from '@/modules/blog/types'

/**
 * Paginación
 *
 * Permite obtener una paginación de publicaciones basada en los filtros
 * argumentados.
 *
 * @param filters
 */
export const getPostPagination = async (filters: PostPaginationFilters) => {
    const response = await client.get('blog/post_pagination', {
        params: filters,
    })

    return PostPagination.convert(response.data)
}

/**
 * Obtener publicación
 *
 * Solicita al servidor la publicación con id argumentado.
 *
 * @param id
 */
export const getPost = async (id: number): Promise<Post> => {
    const response = await client.get(`blog/post/${id}`)

    return Post.convert(response.data)
}

/**
 * Crear publicación
 *
 * Inserta una nueva publicación del blog en Artisan.
 *
 * @param post
 */
export const createPost = async (post: Post): Promise<Post> => {
    const response = await client.post('blog/post', Post.serialize(post))

    return Post.convert(response.data)
}

/**
 * Actualizar publicación
 *
 * @param {Post} post
 * @returns {Promise<Post>}
 */
export const updatePost = async (post: Post): Promise<Post> => {
    const response = await client.put(`blog/post/${post.id}`, Post.serialize(post))

    return Post.convert(response.data)
}

/**
 *
 * Elimina una publicación
 * @param post
 */
export const deletePost = async (post: number): Promise<boolean> => {
    const response = await client.delete(`blog/post/${post}`)

    return response.data.success || false
}

/**
 * Genera una vista previa de una publicación
 *
 * @param post
 */
export const getPostPreview = async (post: Post, locale: string): Promise<{preview_data: string}> => {
    const response = await client.post('blog/post_preview', Post.serialize(post), { params: { locale }})

    return response.data
}

/**
 * Permite obtener la lista de etiquetas registradas en la aplicación
 */
export async function getTagList () {
    const data = await fetch('/blog/tags')
    if (!Array.isArray(data)) {
        throw new Error(`Se espera un array con la lista de etiquetas pero se obtuvo ${typeof data}`)
    }

    return data.map(tag => Tag.deserialize(tag))
}

/**
 * Solicita al servidor la eliminación de una etiqueta
 * @param id
 */
export async function deleteTag (id: number) {
    try {
        return await remove(`/blog/tags/${id}`)
    } catch (e) {
        throw new Error(_.get(e, 'response.data.message', e.message))
    }
}

/**
 * Solicita a la API la creación de una nueva etiqueta
 *
 * @param tag
 */
export async function createTag (tag: Tag) {
    if (!tag.isNew) {
        throw new Error('La etiqueta que se ha intentado insertar ya posee un id')
    }

    const created = await post(`/blog/tag`, tag.serialize())

    return Tag.deserialize(created)
}

export async function updateTag (tag: Tag) {
    if (tag.isNew) {
        throw new Error('No se puede actualizar una etiqueta que no existe')
    }

    const created = await put(`/blog/tag/${tag.id}`, tag.serialize())

    return Tag.deserialize(created)
}
